import { DB_LIMITS } from 'constants/db-limits';
import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * @description Props for the HospitalTotalLabels component
 * @typedef {Object} Props
 * @property {number | undefined} total - The current total count to display
 * @property {keyof typeof DB_LIMITS} translationKey - Key used to access the DB_LIMITS constant and for translations
 */
type Props = {
  total: number | undefined;
  translationKey: keyof typeof DB_LIMITS;
};

/**
 * @description Displays the total count of hospital items along with the database limit.
 * Shows current count versus maximum allowed limit for various entities.
 *
 * @SADtrace SAD-003-3.1, SAD-003-4.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @component
 * @param {Props} props - Component props
 * @param {number | undefined} props.total - The current total count to display
 * @param {keyof typeof DB_LIMITS} props.translationKey - Key used to access the DB_LIMITS constant and for translations
 *
 * @returns {JSX.Element} A div containing the formatted total count and limit information
 *
 * @example
 * // Example usage:
 * <HospitalTotalLabels total={125} translationKey="patients" />
 */
const HospitalTotalLabels: React.FC<Props> = ({ total, translationKey }) => {
  const { t } = useTranslation();

  return (
    <div className="py-2" data-testid="hospital-total-labels">
      {`${t('totalCount.number')} ${t(
        `${translationKey}.title`,
      )}: ${total?.toLocaleString()} (${t('totalCount.limit')}: ${DB_LIMITS[
        translationKey
      ].toLocaleString()})`}
    </div>
  );
};

export default HospitalTotalLabels;
